import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthService } from '@app/core/services/auth.service';
import { EMPTY, Observable } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { QueryParams } from '../types';

@Injectable({
  providedIn: 'root'
})
export class PoMaintenanceService {
  userName: string = '';
  private readonly apiBaseUrl = 'PurchaseOrder/PurchaseOrder';
  private readonly poUpdateFlagCache = new Map<string, Observable<any>>();

  constructor(
    public http: HttpClient,
    private readonly authService: AuthService
  ) {
    this.authService.getCurrentUser().subscribe((user) => {
      this.userName = user?.ApplicationUserName;
    });
  }

  private getUrl(route: string): string {
    return `${this.apiBaseUrl}/${route}`;
  }
  /**
   * Get PO header list
   * @param user: string
   * @param params
   * @returns 
   */
  getPurchaseOrderClassViewLists(user: string, params: QueryParams): Observable<any[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const storedUser = localStorage.getItem('user');
    let userName = user;
    if (!user) {
      userName = storedUser ? JSON.parse(storedUser).ApplicationUserName : '';
    }
    const url = this.getUrl(`GetPurchaseOrderClassViewLists?User=${userName}`);
    return this.http.post<any[]>(url, params, { headers });
  }

  /**
     * Get PO Detail list
     * @param pONumber: string 
     * @param params 
     * @returns 
     */
  getPurchaseOrderDetailsListView(pONumber: string, params: QueryParams): Observable<any[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const url = this.getUrl(`GetPurchaseOrderDetailsListView?PONumber=${pONumber}`);
    return this.http.post<any[]>(url, params, { headers });
  }


  getPurchaseOrderNoList(poNumber: string = ''): Observable<any[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const url = this.getUrl(`GetPurchaseOrderNoList?PONumber=${poNumber}`);
    return this.http.post<any[]>(url, {}, { headers });
  }

  getPurchaseOrderClassByPOId(poNumber: string = ''): Observable<any[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    const url = this.getUrl(`GetPurchaseOrderClassByPOId?PONumber=${poNumber}`);
    return this.http.post<any[]>(url, {}, { headers });
  }

  getPurchaseOrderHeaderStatus(): Observable<any> {
    return this.http.get(`${this.getUrl('GetPurchaseOrderHeaderStatus')}`);
  }

  savePOHeader(payload: any): Observable<any> {
    return this.http.post(`${this.getUrl('SavePurchaseOrder')}`, payload);
  }

  getPurchaseOrderDetailStatus(): Observable<any> {
    return this.http.get(`${this.getUrl('GetPurchaseOrderDetailStatus')}`);
  }

  getPurchaseOrderDetailsViewByLineNo(
    poNumber: string,
    lineNo: string
  ): Observable<any> {
    const params = new HttpParams()
      .set('PONumber', poNumber)
      .set('LineNo', lineNo);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this.http.post<any>(this.getUrl('GetPurchaseOrderDetailsViewByLineNo'), {}, { headers: headers, params });
  }

  savePODetail(payload: any): Observable<any> {
    return this.http.post(`${this.getUrl('SavePurchaseOrderDetails')}`, payload);
  }


  copyPOHeader(payload: any): Observable<any> {
    return this.http.post(`${this.getUrl('CopyPurchaseOrderHeader')}`, payload);
  }

  copyPODetail(payload: any): Observable<any> {
    return this.http.post(`${this.getUrl('CopyPurchaseOrderDetails')}`, payload);
  }

  deletePOHeader(payload: any): Observable<any> {
    return this.http.request('DELETE', `${this.getUrl('DeletePurchaseOrderHeader')}`, {
      body: payload
    });
  }

  deletePODetail(payload: any): Observable<any> {
    return this.http.request('DELETE', `${this.getUrl('DeletePurchaseOrderDetails')}`, {
      body: payload
    });
  }

  POUpdate(userId: string): Observable<any> {
    let payload = {
      User: userId
    }
    return this.http.post(`/PurchaseOrder/PurchaseOrder/PurchaseOrderUpdate`, payload);
  }

  getPOUpdateFlag(userID: string, forceRefresh = false): Observable<any> {
    if (!userID) {
      return EMPTY;
    }

    if (forceRefresh) {
      this.poUpdateFlagCache.delete(userID);
    }

    if (!this.poUpdateFlagCache.has(userID)) {
      const params = new HttpParams().set('User', userID);
      const request$ = this.http
        .get(`/PurchaseOrder/PurchaseOrder/GetPurchaseOrderProcessState`, {
          params
        })
        .pipe(shareReplay(1));
      this.poUpdateFlagCache.set(userID, request$);
    }

    return this.poUpdateFlagCache.get(userID)!;
  }

  invalidatePOUpdateFlagCache(userID?: string): void {
    if (userID) {
      this.poUpdateFlagCache.delete(userID);
    } else {
      this.poUpdateFlagCache.clear();
    }
  }

  exportPurchaseOrderLists(userID: string): Observable<any> {
    const params = new HttpParams()
      .set('User', userID);
    return this.http.get(`${this.getUrl('ExportPurchaseOrderLists')}`, { params });

  }

  /**
   * Get Purchase Order PO Approval Details List
   * @param pONumber
   * @param params
   */
  getPurchaseOrderPOApprovalDetailsList(pONumber: string, params: QueryParams): Observable<any[]> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    const url = this.getUrl(`GetPurchaseOrderPOApprovalDetailsList?PONumber=${pONumber}`);
    return this.http.post<any[]>(url, params, { headers });
  }

  getPurchaseOrderApprovalStatus(): Observable<any> {
    return this.http.get(`${this.getUrl('GetPurchaseOrderHeaderApprovalStatus')}`);
  }

  pOApprovalUpdate(payload: any): Observable<any> {
    return this.http.post(this.getUrl(`POApprovalUpdate`), payload);
  }

  savePurchaseOrderPOApproval(payload: any): Observable<any> {
    return this.http.post(this.getUrl(`SavePurchaseOrderPOApproval`), payload);
  }

  getPOApprovalDetailsListByUser(
    poNumber: string,
    user: string,
    POClass: string,
    seqNumber: string,
    currentUser: string
  ): Observable<any> {
    const params = new HttpParams()
      .set('PONumber', poNumber)
      .set('User', user)
      .set('SequenceNumber', seqNumber)
      .set('POClass', POClass)
      .set('PONumber', poNumber)
      .set('CurrentUser', currentUser);

    const headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });
    return this.http.get<any>(this.getUrl('GetPurchaseOrderPOApprovalDetailsListByuser'), { params });
  }


}